import { Component, EventEmitter, Input, OnInit, ViewChild } from "@angular/core";
import { MatTable, MatTableDataSource } from "@angular/material/table";

import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AlertController, ModalController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { tap } from "rxjs/operators";
import { FairAppSimpleRequest } from "../../../api/models/fair-app-simple-request";
import { OdataAktOrtDetailResponse } from "../../../api/models/odata-akt-ort-detail-response";
import { OdataAktOrtResponse } from "../../../api/models/odata-akt-ort-response";
import { AktService } from "../../../api/services/akt.service";
import { UserRole } from "../../enums/global-enums";
import { AuthService } from "../../services/auth.service";
import { LoveItToastService } from "../../services/love-it-toast.service";
import { AktGeraetComponent } from "../akt-geraet/akt-geraet.component";

@Component({
  selector: "app-akt-geraete-list",
  templateUrl: "./akt-geraete-list.component.html",
  styleUrls: ["./akt-geraete-list.component.scss"],
  standalone: false,
})
export class AktGeraeteListComponent implements OnInit {
  @Input() openCreateModalEvent!: EventEmitter<void>;
  @Input({ required: true }) aktId: number;
  @Input({ required: true }) detail: number;
  @Input() disabled: boolean;
  geraete: OdataAktOrtDetailResponse[];
  @Input() aktOrte: OdataAktOrtResponse[];
  geraeteFetchFailed: boolean;
  sortWasChanged: boolean;
  uploadingSortChanges: boolean;
  fetchingGeraete: boolean;

  displayedColumns: string[] = ["Nummer", "Bezeichnung", "Anzahl", "TableButtons"];

  @ViewChild("matTable", { static: true }) matTable: MatTable<any>;

  dataSource: MatTableDataSource<any>;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private alertController: AlertController,
    private modalController: ModalController,
    private aktService: AktService,
    private loveItToastService: LoveItToastService,
    protected authService: AuthService,
  ) {
    this.dataSource = new MatTableDataSource([]);
  }

  ngOnInit() {
    this.getAktGeraete();
    if (this.openCreateModalEvent) {
      this.openCreateModalEvent.subscribe(() => {
        this.tryOpenGeraetModal();
      });
    }
  }

  getAktGeraete() {
    this.geraete = null;
    if (this.dataSource) {
      this.displayGeraete();
    }

    this.fetchingGeraete = true;
    this.aktService
      .aktAktortdetailOdataRetrieveCreate({
        akt_id: this.aktId,
        token: this.oidcSecurityService.getToken(),
      } as FairAppSimpleRequest)
      .subscribe({
        next: (response: OdataAktOrtDetailResponse[]) => {
          response.forEach((geraet) => {
            if (geraet.Ort) {
              geraet.Ort = undefined;
            }
          });
          response.sort((a, b) => a.Nummer - b.Nummer);
          this.geraete = response;
          this.dataSource = new MatTableDataSource(this.geraete);
          this.displayGeraete();
          this.fetchingGeraete = false;
        },
        error: (errorResponse) => {
          this.geraeteFetchFailed = true;
          this.fetchingGeraete = false;
        },
      });
  }

  displayGeraete() {
    this.dataSource.data = Array.from(this.geraete || []);
  }

  onDatatableActivate(event, row: OdataAktOrtDetailResponse) {
    if (event.target.id === "delete-geraet-button") {
      return;
    }
    this.tryOpenGeraetModal(row);
  }

  async deleteGeraetButton(geraet: OdataAktOrtDetailResponse) {
    const alert = await this.alertController.create({
      header: `Das Gerät ${geraet?.Bezeichnung ? `"${geraet.Bezeichnung}"` : ""} löschen?`,
      message: "Gelöschte Geräte können nicht wiederhergestellt werden!",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-danger",
          text: "Löschen",
          handler: () => {
            this.tryDeleteGeraet(geraet.Oid);
          },
        },
      ],
    });
    document.body.appendChild(alert);
    await alert.present();
  }

  async tryDeleteGeraet(oid: number) {
    if (this.sortWasChanged) {
      await this.tryModifyingEntries(() => this.deleteGeraet(oid));
    } else {
      await this.deleteGeraet(oid);
    }
  }

  async deleteGeraet(oid: number) {
    this.aktService
      .aktAktortdetailOdataDeleteCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
        Oid: oid,
      })
      .subscribe({
        next: (response) => {
          this.dataSource.data = this.dataSource.data.filter((entry) => entry.Oid != oid);
          this.uploadSortingOfGeraete().subscribe();
        },
        error: async (error) => {
          const message = `Fehler beim Löschen des Gerätes!`;
          this.loveItToastService.showToastMessage(message);
        },
      });
  }

  async tryModifyingEntries(modifyingFunction: Function) {
    const alert = await this.alertController.create({
      header: "Ungespeicherte Reihenfolge vorhanden",
      message: "Möchten Sie die Änderungen der Reihenfolge speichern?",
      buttons: [
        {
          text: "Verwerfen",
          cssClass: "color-primary",
          handler: () => {
            this.resetLocalGeraeteSort();
            modifyingFunction();
          },
        },
        {
          cssClass: "color-secondary",
          text: "Speichern",
          handler: () => {
            this.uploadSortingOfGeraete().subscribe(() => {
              modifyingFunction();
            });
          },
        },
      ],
    });
    await alert.present();
  }

  async tryOpenGeraetModal(existingGeraet: OdataAktOrtDetailResponse = null) {
    if (this.uploadingSortChanges || this.fetchingGeraete) {
      return;
    }
    if (this.sortWasChanged) {
      await this.tryModifyingEntries(() => this.openGeraetModal(existingGeraet));
    } else {
      await this.openGeraetModal(existingGeraet);
    }
  }

  async openGeraetModal(existingGeraet: OdataAktOrtDetailResponse = null) {
    if (!existingGeraet) {
      existingGeraet = {
        Nummer: this.getNewGeraetNummer(),
      } as OdataAktOrtDetailResponse;
    }
    const modal = await this.modalController.create({
      component: AktGeraetComponent,
      cssClass: ["bigger-modal", "resizeable-modal"],
      backdropDismiss: false,
      componentProps: {
        aktId: this.aktId,
        existingGeraet: existingGeraet,
        detail: this.detail,
        aktOrte: this.aktOrte,
        disabled: this.disabled,
      },
    });

    modal.onDidDismiss().then((data) => {
      const d = data.data;
      if (data?.role === "confirm") {
        this.getAktGeraete();
      }
    });

    await modal.present();
  }

  drop(event: CdkDragDrop<string>) {
    if (this.uploadingSortChanges) {
      this.loveItToastService.showToastMessage("Sortierung darf nicht während eines Uploads geändert werden.");
      return;
    }
    const previousIndex = this.dataSource.data.findIndex((d) => d === event.item.data);

    moveItemInArray(this.dataSource.data, previousIndex, event.currentIndex);
    this.updateGeraeteNummerFields();
    this.dataSource._updateChangeSubscription();
  }

  updateGeraeteNummerFields() {
    this.dataSource.data.forEach((geraet, index) => {
      if (geraet.Nummer != index + 1) {
        this.sortWasChanged = true;
        geraet.Nummer = index + 1;
      }
    });
  }

  resetLocalGeraeteSort() {
    this.dataSource.data = Array.from(this.geraete || []);
    this.updateGeraeteNummerFields();
    this.dataSource._updateChangeSubscription();
    this.sortWasChanged = false;
  }

  uploadSortingOfGeraete() {
    this.uploadingSortChanges = true;

    return this.aktService
      .aktAktortdetailOdataPatchMultipleCreate({
        akt_id: this.aktId,
        token: this.oidcSecurityService.getToken(),
        akt_ort_details: this.dataSource.data.map((geraet) => geraet as OdataAktOrtDetailResponse),
      })
      .pipe(
        tap(() => {
          this.sortWasChanged = false;
          this.uploadingSortChanges = false;
          this.getAktGeraete();
        }),
      );
  }

  getNewGeraetNummer() {
    return this.dataSource.data.length + 1;
  }

  protected readonly UserRole = UserRole;
}
